// TelcoTrack — shared expandable product-category tree (Sklady / Produkty filter / Kategorie manage)

import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  productCategoryTreeClickAction,
  productCategoryTreeManageRowPadding,
  productCategoryTreeRowPadding,
  type ProductCategoryTreeNode,
} from '../../utils/product-category-tree-node.util';
import { productCategoryFilterNodeLabelClass } from '../../utils/category-tree-table-styles.util';

export type ProductCategoryTreeMode = 'select' | 'manage';

@Component({
  selector: 'app-product-category-tree',
  templateUrl: './product-category-tree.component.html',
  styleUrls: ['./product-category-tree.component.scss'],
})
export class ProductCategoryTreeComponent implements OnChanges {
  @Input() nodes: ProductCategoryTreeNode[] = [];
  @Input() mode: ProductCategoryTreeMode = 'select';
  @Input() selectedId: string | null = null;
  @Input() showAllOption = false;
  @Input() allOptionLabel = 'Vše';
  @Input() emptyMessage = 'Katalog je prázdný.';
  @Input() canEdit = false;
  /**
   * Manage mode: show „Vlastní specifikace“ in the ⋮ menu (Zařízení › Kategorie).
   * Materials catalog leaves this false.
   */
  @Input() showManageSpecs = false;
  /** Manage mode: label for the rename/edit row action. */
  @Input() renameMenuLabel = 'Přejmenovat';
  /** When set, expansion is controlled by the parent (manage view). */
  @Input() expandedIds: string[] | null = null;
  /** Merge these ids into expanded set (e.g. after creating a child). */
  @Input() expandNodeIds: string[] = [];
  /**
   * Select mode: leaf click selects the category. Folder click only expands/collapses
   * unless `selectFolderOnClick` (Zásoby: expand + filter the stock table).
   */
  @Input() selectionEnabled = true;
  /**
   * Folder click expands/collapses AND selects (right pane filters in place).
   * Used on Zásoby. Default false keeps Katalog folders toggle-only.
   */
  @Input() selectFolderOnClick = false;

  @Output() selectedIdChange = new EventEmitter<string | null>();
  @Output() nodeSelect = new EventEmitter<ProductCategoryTreeNode | null>();
  /** Double-click a folder (explicit open). Katalog may use this; Zásoby filters in place. */
  @Output() nodeActivate = new EventEmitter<ProductCategoryTreeNode>();
  @Output() expandedIdsChange = new EventEmitter<string[]>();
  @Output() addChild = new EventEmitter<ProductCategoryTreeNode>();
  @Output() rename = new EventEmitter<ProductCategoryTreeNode>();
  @Output() delete = new EventEmitter<ProductCategoryTreeNode>();
  @Output() manageSpecs = new EventEmitter<ProductCategoryTreeNode>();

  private internalExpanded = new Set<string>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['expandNodeIds'] && this.expandNodeIds.length) {
      for (const id of this.expandNodeIds) this.internalExpanded.add(id);
    }
  }

  get hasContent(): boolean {
    return this.showAllRow || this.nodes.length > 0;
  }

  get showAllRow(): boolean {
    return this.showAllOption && this.selectionEnabled;
  }

  isExpanded(id: string): boolean {
    if (this.expandedIds) return this.expandedIds.includes(id);
    return this.internalExpanded.has(id);
  }

  rowPadding(depth: number): number {
    return this.mode === 'manage'
      ? productCategoryTreeManageRowPadding(depth)
      : productCategoryTreeRowPadding(depth);
  }

  trackNode = (_: number, node: ProductCategoryTreeNode): string => node.id;

  onAllClick(): void {
    if (!this.selectionEnabled) return;
    this.selectedId = null;
    this.selectedIdChange.emit(null);
    this.nodeSelect.emit(null);
  }

  onSelectRowClick(event: MouseEvent, node: ProductCategoryTreeNode): void {
    const action = productCategoryTreeClickAction({
      hasChildren: this.nodeHasChildren(node),
      clickDetail: event.detail,
      selectionEnabled: this.selectionEnabled,
      selectFolderOnClick: this.selectFolderOnClick,
    });
    if (action === 'toggle' || action === 'toggle-select') {
      this.setExpanded(node.id, !this.isExpanded(node.id));
      if (action === 'toggle-select') this.emitNodeSelection(node);
      return;
    }
    if (action === 'select') this.emitNodeSelection(node);
  }

  onSelectRowDblClick(node: ProductCategoryTreeNode): void {
    if (this.nodeHasChildren(node)) {
      this.setExpanded(node.id, true);
    }
    this.nodeActivate.emit(node);
    if (this.selectionEnabled) this.emitNodeSelection(node);
  }

  onManageRowClick(node: ProductCategoryTreeNode): void {
    // Leaves: no expand chevron / toggle, but still emit selection (pickers + highlight).
    if (this.nodeHasChildren(node)) {
      this.setExpanded(node.id, !this.isExpanded(node.id));
    }
    this.emitNodeSelection(node);
  }

  onAddChildFromMenu(node: ProductCategoryTreeNode, event: Event): void {
    event.stopPropagation();
    this.closeMenu(event);
    this.setExpanded(node.id, true);
    this.addChild.emit(node);
  }

  onRenameFromMenu(node: ProductCategoryTreeNode, event: Event): void {
    event.stopPropagation();
    this.closeMenu(event);
    this.rename.emit(node);
  }

  onDeleteFromMenu(node: ProductCategoryTreeNode, event: Event): void {
    event.stopPropagation();
    this.closeMenu(event);
    this.delete.emit(node);
  }

  onManageSpecsFromMenu(node: ProductCategoryTreeNode, event: Event): void {
    event.stopPropagation();
    this.closeMenu(event);
    this.manageSpecs.emit(node);
  }

  onRename(node: ProductCategoryTreeNode): void {
    this.rename.emit(node);
  }

  onDelete(node: ProductCategoryTreeNode): void {
    this.delete.emit(node);
  }

  private closeMenu(event: Event): void {
    const details = (event.target as HTMLElement | null)?.closest('details');
    if (details) details.open = false;
  }

  showManageChevron(node: ProductCategoryTreeNode): boolean {
    return this.nodeHasChildren(node);
  }

  nodeHasChildren(node: ProductCategoryTreeNode): boolean {
    return (node.children?.length ?? 0) > 0;
  }

  nodeLabelClass(node: ProductCategoryTreeNode): string {
    return productCategoryFilterNodeLabelClass(this.nodeHasChildren(node));
  }

  canRenameNode(node: ProductCategoryTreeNode): boolean {
    return !(node.isSystemRoot ?? false);
  }

  canDeleteNode(node: ProductCategoryTreeNode): boolean {
    return !(node.isSystemRoot ?? false);
  }

  private emitNodeSelection(node: ProductCategoryTreeNode): void {
    this.selectedId = node.id;
    this.selectedIdChange.emit(node.id);
    this.nodeSelect.emit(node);
  }

  private setExpanded(id: string, expanded: boolean): void {
    if (this.expandedIds) {
      const next = new Set(this.expandedIds);
      if (expanded) next.add(id);
      else next.delete(id);
      this.expandedIdsChange.emit([...next]);
      return;
    }
    const next = new Set(this.internalExpanded);
    if (expanded) next.add(id);
    else next.delete(id);
    this.internalExpanded = next;
  }
}
